export interface IAssignedItem {
  id: number,
  name: string,
  components: IAssignedItemComponent[],
  claimedBatches?: Array<{
    id: number,
    created_on: string,
    is_marked: number,
    marking_window_item_id: number,
  }>,
  isAvailable:boolean,
  dateTimeAssigned: string,
  dateWindowStart: string,
  dateWindowEnd: string,
  numBatchesAvail: number, // int
  numBatchesClaimed: number, // int
  numBatchesScored: number,
  dateTimeBatchesDue?: string,
  maxBatchNumClaim?: number,
  maxBatchNumClaimTotal?: number, // total batches claimable per scorer for this item (null = no limit)
  isBatchClaimTotalLimitReached?: boolean,
  claim_dur_hours?: number,
  isScorerReportEnabled?: boolean,
  qt_cut_score?: number,
  /** DELIV-17370 — scoring on this item is inside the window's non-working hours right now */
  isOutsideOperatingHours?: boolean,
  operatingScheduleReason?: string,
  /** When SCORING opens again in the group timezone, e.g. `Fri 11:00` */
  operatingScheduleResumesAt?: string,
  /** Set only when the API searched this many days and found no opening */
  operatingScheduleNoOpeningWithinDays?: number,
  /** The window hides when non-working hours end; the API omits the time and the preview */
  isNwhResumeTimeHidden?: boolean,
  /** Read-only rules of every group gating this item */
  operatingSchedulePreviewGroups?: IOperatingSchedulePreviewGroup[],
  /** Session pause — marking_windows.is_scoring_disabled */
  isMarkingWindowPaused?: boolean,
  /** BUG-005 — now is after the window's end date; the API refuses every claim and task */
  isMarkingWindowClosed?: boolean,
  /** BUG-005 — now is before the window's start date */
  isMarkingWindowNotOpen?: boolean,
  __desiredClaimQuantity?: number, // int
  /** SEL-015 — text of the last claim attempt for the "Claim result" status region */
  __claimResult?: string,
  __isRecomputingBatches?:boolean,
  __showOperatingSchedule?: boolean,
  __schedulePreviewTaskType?: AssignedItemComponentType,
  /** Memoized preview groups — a stable reference for the preview binding */
  __schedulePreviewGroups?: any[],
}

export interface IOperatingSchedulePreviewGroup {
  id?: number,
  name: string,
  timezone: string,
  ranges: any[],
}
export interface IAssignedItemComponent {
  id: number,
  componentType: AssignedItemComponentType,
  caption: string,
  status: AssignedItemStatus,
  allowRevisit?:boolean,
  isPassFail?:boolean,
  /** DELIV-17370 — this task type is inside the window's non-working hours right now */
  isOutsideOperatingHours?: boolean,
  operatingScheduleReason?: string,
  operatingScheduleResumesAt?: string,
  operatingScheduleNoOpeningWithinDays?: number,
  stats?: {
      caption:string,
  }[]
}
export enum AssignedItemStatus {
    PENDING = 'PENDING',
    NOT_AVAILABLE = 'NOT_AVAILABLE',
    ACTIVE = 'ACTIVE',
    COMPLETED = 'COMPLETED',
    FAILED = 'FAILED',
    SKIPPED = 'SKIPPED',
    DISABLED = 'DISABLED',
}
export enum AssignedItemComponentType {
    TRAINING_MATERIALS = 'TRAINING_MATERIALS',
    PRACTICE = 'PRACTICE',
    QUALIFYING = 'QUALIFYING',
    SCORING = 'SCORING',
}

/** Same sequence as default `marking_item_marker_tasks` rows created on scorer invite. */
export const ASSIGNED_ITEM_COMPONENT_TYPE_ORDER: AssignedItemComponentType[] = [
  AssignedItemComponentType.TRAINING_MATERIALS,
  AssignedItemComponentType.PRACTICE,
  AssignedItemComponentType.QUALIFYING,
  AssignedItemComponentType.SCORING,
];

export const getAssignedItemComponentTypeCaption = (
  componentType: AssignedItemComponentType,
): string => {
  switch (componentType) {
    case AssignedItemComponentType.TRAINING_MATERIALS: return 'Training Materials';
    case AssignedItemComponentType.PRACTICE: return 'Practice Test';
    case AssignedItemComponentType.QUALIFYING: return 'Qualifying Test';
    case AssignedItemComponentType.SCORING: return 'Scoring';
    default: return componentType;
  }
};

/** Checkbox options for marking-task selectors (blackout rules, etc.). */
export const ASSIGNED_ITEM_MARKING_TASK_OPTIONS: {
  id: AssignedItemComponentType;
  caption: string;
}[] = ASSIGNED_ITEM_COMPONENT_TYPE_ORDER.map((id) => ({
  id,
  caption: getAssignedItemComponentTypeCaption(id),
}));

/**
 * Preparation task types (not Scoring). Used with `allow_revisit` on completed PT/QT rows.
 * Training Materials are always reopenable — see `isCompletedTaskOpen`.
 */
export const isRevisitableTaskType = (
  componentType: AssignedItemComponentType,
): boolean => {
  switch (componentType) {
    case AssignedItemComponentType.TRAINING_MATERIALS:
    case AssignedItemComponentType.PRACTICE:
    case AssignedItemComponentType.QUALIFYING:
      return true;
    default:
      return false;
  }
};

/**
 * Whether a completed task row should be a live link.
 *
 * - Training Materials: always (read-only revisit; no new attempt).
 * - Practice / Qualifying: `allow_revisit` on the marker task (profile default PT=1, QT=0).
 * - Scoring: `allow_revisit` and claimed batches (unchanged).
 */
export const isCompletedTaskOpen = (
  componentType: AssignedItemComponentType,
  allowRevisit?: boolean,
): boolean => {
  if (componentType === AssignedItemComponentType.TRAINING_MATERIALS) {
    return true;
  }
  if (isRevisitableTaskType(componentType)) {
    return !!allowRevisit;
  }
  return !!allowRevisit;
};

/**
 * Reopening a completed Practice or Qualifying task starts a **new attempt** on the
 * next response set — `getTrainingBatch` has no unmarked batch to resume, so it creates
 * one. Training Materials is just re-read, with no new attempt.
 */
export const isCompletedTaskNewAttempt = (
  componentType: AssignedItemComponentType,
): boolean => {
  switch (componentType) {
    case AssignedItemComponentType.PRACTICE:
    case AssignedItemComponentType.QUALIFYING:
      return true;
    default:
      return false;
  }
};

export const getTrainingTypeCaption = (trainingType: AssignedItemComponentType) => {
  switch (trainingType){
    case AssignedItemComponentType.TRAINING_MATERIALS: return 'Training Materials';
    case AssignedItemComponentType.PRACTICE: return 'Practice Test';
    case AssignedItemComponentType.QUALIFYING:  return 'Qualifying Test';
  }
}
